import {
  addHours, isAfter, isBefore,
  roundToNearestMinutes,
} from 'date-fns';
import { TFunction } from 'i18next';
import { CAPTAIN_OPTION_1_INCLUDED_IN_PRICE, CAPTAIN_OPTION_2_INDEPENDENT_CAPTAIN } from 'src/common/constants';
import { timeStringToDate } from 'src/common/helpers';
import { BlockedTime } from 'src/common/providers/useBookingMotivators';
import { AuthUserApiResponse } from 'src/common/types/api/v4/auth/user';
import { FullBoatSearchDetail } from 'src/common/types/api/v4/boats/search';
import { CaptainOptionValue } from 'src/common/types/CaptainOptions';
import { convertToTimeZone } from 'src/common/utils/dateHelpers';
import { FULL_DAY_HOURS } from 'src/listing/constants';
import {
  getDurationHours, UnitPriceDetail,
} from 'src/listing/helper';
import { DepartureTime, TripCartFormState } from 'src/listing/types';
import {
  getDurationOptions, TripCartDurationOption,
} from 'src/trip-cart/utils/tripCartUtils';

/**
 * These are the possible options the form can be changed to given its current state (form values).
 * Initial values are also part of these options, merged together.
 *
 * In some fields, an array is used, this means there are several possibilities to change to. If
 * the array is length 2 or more, there are other values this field can be changed to. Otherwise
 * there are no other values to change to.
 *
 * In some fields, there is only one value. This means it is configured for only one result ever,
 * or else it is a field that is not part of the form, and is instead derived from form data.
 */
export type TripCartOptions = {
  // Same keys as TripCartFormState
  // All possible form durations
  formDuration: TripCartDurationOption[];
  // All captain options
  withCaptain: CaptainOptionValue[];
  // Can be true or false or both
  isMultiday: boolean[];
  // Maximum capacity. Minimum is 1, used directly on the form itself
  formGuests: number; // maximum capacity
  // custom fields
} & {
  // Descriptive text for what the selected or only captain option means
  captainText: CaptainText;
  // If captains are included, the price can have a separate captain line item
  hasCaptainFee: boolean;
  // Instabooks are confirmed prices, inquiries are estimates
  isCostConfirmed: boolean;
  calendar: {
    // all other dates will be disabled. If undefined, all dates are available
    enableSpecificDates: string[] | undefined;
  };
  // CTAs
  actions: Actions;
  errors?: {
    [field in keyof Pick<TripCartFormState, 'departureTime' | 'withCaptain'>]?: ErrorMessage;
  };
};

type CaptainText = {
  // Used for translation keys
  key: 'self-captain' | 'with-captain' | 'with-captain--separate-fee';
  hasDescription: boolean;
  hasTooltip: boolean;
};

type Actions = {
  instabookEnabled: boolean;
  inquiryEnabled: boolean;
  isOwnBoat: boolean;
};

type ErrorMessage = string;

type GetCaptainInfoReturn = {
  withCaptain: TripCartOptions['withCaptain'];
  captainText: TripCartOptions['captainText'];
  hasCaptainFee: TripCartOptions['hasCaptainFee'];
  error?: ErrorMessage;
};

type ListingFeatureFlags = {
  summary?: boolean;
  instabook?: boolean;
};

export function getCaptainInfo({
  listing, values, featureFlags,
}: {
  values: TripCartFormState;
  listing: FullBoatSearchDetail;
  featureFlags?: ListingFeatureFlags;
}): GetCaptainInfoReturn {
  let hasCaptainFee = true;
  const captainText: Partial<CaptainText> = {
    hasDescription: true,
    hasTooltip: true,
  };

  const withCaptain = listing.captain_options;

  // Stop calculating, invalid option
  if (!withCaptain.includes(values.withCaptain)) {
    return {
      // include valid options it should be changed to
      withCaptain,
      // Values avoid component crashes
      captainText: {} as CaptainText,
      hasCaptainFee,
      // Error is discarded, only used in logic. No need to translate
      error: 'invalid',
    };
  }

  if (values.withCaptain === CAPTAIN_OPTION_1_INCLUDED_IN_PRICE) {
    captainText.key = 'with-captain';
    hasCaptainFee = false;
  } else if (values.withCaptain === CAPTAIN_OPTION_2_INDEPENDENT_CAPTAIN) {
    const captainCost = listing.captain_hourly_cost;
    if (!captainCost
      || values.isMultiDay
      || getDurationHours(values.formDuration) === FULL_DAY_HOURS
    ) {
      if (featureFlags?.summary) {
        captainText.key = 'with-captain';
      } else {
        captainText.key = 'with-captain--separate-fee';
      }
      captainText.hasTooltip = false;
    } else {
      captainText.key = 'with-captain';
      captainText.hasDescription = false;
      captainText.hasTooltip = false;
    }
  } else {
    // CAPTAIN_OPTION_4_NOT_INCLUDED_IN_PRICE
    captainText.key = 'self-captain';
    hasCaptainFee = false;
  }

  return {
    hasCaptainFee,
    captainText: captainText as CaptainText,
    withCaptain,
  };
}

export function getActions({
  user,
  values,
  listing,
  isDateUnavailable,
  previewMode,
  hasErrors,
  featureFlags,
}: {
  user: Partial<AuthUserApiResponse> | null;
  values: TripCartFormState;
  listing: FullBoatSearchDetail;
  isDateUnavailable: (dates: Date[], isMultiDay: boolean) => boolean;
  previewMode: boolean;
  hasErrors?: boolean;
  featureFlags?: ListingFeatureFlags;
}): Actions {
  const isUnavailable = isDateUnavailable(values.formSelectedDates, values.isMultiDay);

  const isOwnBoat = user?.id === listing.user_details.id;
  const instabookEnabled = (!values.isMultiDay
    && listing.has_available_instabook
    && featureFlags?.instabook
  ) ?? false;
  return {
    inquiryEnabled: !hasErrors && !isUnavailable && !previewMode,
    isOwnBoat,
    instabookEnabled,
  };
}

export function roundedDepartureTime(departureTime: string, date?: Date): Date {
  const value = timeStringToDate(departureTime.toString(), date);
  // Only allows 15 minute increments
  return roundToNearestMinutes(value, { nearestTo: 15 });
}

export function validateDepartureTime({
  value, durationHours, blockedTimes, date, listingTimeZone, t,
}: {
  value: string;
  durationHours: number;
  blockedTimes: BlockedTime[];
  date: Date;
  listingTimeZone: string | undefined;
  t: TFunction;
}): { error: ErrorMessage } | undefined {
  if (!listingTimeZone) return undefined;

  const MIN_HOURS_BEFORE_INQUIRY = 1;
  // TODO: check min hour requirement
  const currentDateTime = convertToTimeZone(
    addHours(new Date(), MIN_HOURS_BEFORE_INQUIRY),
    listingTimeZone,
  );

  // Don't round here, as can seem confusing for free text entry
  const selectedDateTime = timeStringToDate(value, date);
  const isDateInFuture = isBefore(currentDateTime, selectedDateTime);

  if (!isDateInFuture) {
    return { error: t('trip-cart--departure-time--error-past') };
  }

  const selectedDateTimeDuration = addHours(selectedDateTime, durationHours);
  const blocked = blockedTimes.find((range) => {
    const start = timeStringToDate(range.start_time, date);
    const end = timeStringToDate(range.end_time, date);

    return isAfter(selectedDateTimeDuration, start) && isBefore(selectedDateTime, end);
  });

  if (blocked) {
    return {
      error:
        t(
          'trip-cart--departure-time--error-blocked',
          { startTime: blocked.start_time, endTime: blocked.end_time },
        ),
    };
  }
  return undefined;
}

function getDepartureTime({
  values, departureTimes, blockedTimes, listingTimeZone, hasSubmitted, t,
}: {
  values: TripCartFormState;
  // Undefined for inquiry
  departureTimes: DepartureTime[] | undefined;
  blockedTimes: BlockedTime[];
  listingTimeZone: string | undefined;
  hasSubmitted?: boolean;
  t: TFunction;
}): { error?: ErrorMessage } {
  // TODO: id
  const value = values.departureTime ? values.departureTime : undefined;

  // free-entry
  if (value && !departureTimes) {
    const durationHours = getDurationHours(values.formDuration);
    const result = validateDepartureTime({
      value,
      durationHours,
      date: values.formSelectedDates[0],
      blockedTimes,
      listingTimeZone,
      t,
    });

    // Keep value, even if error since this is free-entry
    return { error: result?.error };
  }
  // instabook: explicit entry
  if (value && departureTimes?.find((time) => time.formattedTime24 === value)) {
    return { error: undefined };
  }

  if (value && departureTimes?.length) {
    return { error: t('trip-cart--form-error--departure-time') };
  }

  if (!value && hasSubmitted) {
    return { error: t('trip-cart--form-error--departure-time') };
  }
  // No concerns, all errors already caught
  return { error: undefined };
}

function getTripCartOptions({
  user,
  values,
  unitPriceDetail,
  listing,
  isDateUnavailable,
  departureTimes,
  blockedTimes,
  previewMode,
  hasSubmitted,
  featureFlags,
  t,
}: {
  user: Partial<AuthUserApiResponse> | null;
  values: TripCartFormState;
  unitPriceDetail: UnitPriceDetail;
  listing: FullBoatSearchDetail;
  isDateUnavailable: (dates: Date[], isMultiDay: boolean) => boolean;
  departureTimes: DepartureTime[] | undefined;
  blockedTimes: BlockedTime[];
  previewMode: boolean;
  hasSubmitted: boolean;
  t: TFunction;
  featureFlags?: ListingFeatureFlags;
}): TripCartOptions {
  // Durations
  const formDuration = getDurationOptions(unitPriceDetail, values.isMultiDay);

  const calendarSeasons = listing.calendar_seasons;
  // Multiday
  const isMultiday = [
    calendarSeasons?.has_same_day_pricing as boolean,
    calendarSeasons?.has_multiple_pricing as boolean,
  ];

  // Captain
  const {
    captainText, hasCaptainFee, withCaptain, error: withCaptainError,
  } = getCaptainInfo({
    listing,
    values,
    featureFlags,
  });

  const calendar = {
    enableSpecificDates: undefined,
  };

  const { error: departureTimeError } = getDepartureTime({
    values,
    departureTimes,
    blockedTimes,
    listingTimeZone: listing.timezone,
    hasSubmitted,
    t,
  });

  const errors: TripCartOptions['errors'] = {
    withCaptain: withCaptainError,
    departureTime: departureTimeError,
  };

  // Remove any keys that have no value
  Object.entries(errors).forEach(([key, value]) => {
    if (!value) delete errors[key];
  });

  // Actions
  const actions = getActions({
    user,
    listing,
    values,
    isDateUnavailable,
    previewMode,
    hasErrors: Object.keys(errors).length > 0,
    featureFlags,
  });

  return {
    formDuration,
    withCaptain,
    isMultiday,
    formGuests: listing.capacity,
    // Always the same as the action
    isCostConfirmed: actions.instabookEnabled,
    captainText,
    hasCaptainFee,
    calendar,
    actions,
    errors: Object.keys(errors).length ? errors : undefined,
  };
}

export default getTripCartOptions;
